import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import { AuthContext } from '@healthdesk/shared-hooks';
import {
  DISPATCH_COMPLETED,
  DISPATCH_EXPIRED,
  DISPATCH_INVALID_CODE,
  DISPATCH_NOT_FOUND,
  completeConversationDispatch,
  getNormalizedPatientData,
  markDispatchStarted,
  validateConversationDispatch,
} from '@healthdesk/shared-services';
import CheckInConversation from '../../pages/checkin/CheckInConversation';
import { llmProviderService } from '../../services/llm/llmProviderService';
import { contentCardSx } from '../../styles/standaloneLayoutTokens';

const PENDING_DISPATCH_STORAGE_KEY = 'pendingConversationDispatch';

function getDispatchErrorMessage(error) {
  if (error?.code === DISPATCH_EXPIRED) {
    return 'This conversation link has expired. Please ask your care team for a new link.';
  }
  if (error?.code === DISPATCH_COMPLETED) {
    return 'This conversation has already been completed.';
  }
  if (error?.code === DISPATCH_INVALID_CODE || error?.code === DISPATCH_NOT_FOUND) {
    return 'This link is invalid.';
  }
  return 'We could not open this conversation. Please try again or ask your care team for help.';
}

function mapPatientCmr(normalized) {
  if (!normalized) return null;
  return {
    demographics: {
      firstName: normalized.firstName || '',
      lastName: normalized.lastName || '',
      dateOfBirth: normalized.dateOfBirth || null,
      sex: normalized.sex || null,
      email: normalized.email || null,
      phone: normalized.phone || null,
      address: normalized.address || null,
      emergencyContact: normalized.emergencyContact || null,
    },
    medications: normalized.medications || [],
    allergies: normalized.allergies || [],
    pmh: normalized.pmh || [],
    psh: normalized.psh || [],
    familyHistory: normalized.familyHistory || [],
    insurance: normalized.insurance || null,
  };
}

function isPatientConversationRole(role) {
  return role === 'patient' || role === 'p2' || role === 'p4' || role === 'student';
}

async function extractRosResults(conversationResults) {
  if (!conversationResults?.findings?.length && !conversationResults?.transcript?.length) {
    return null;
  }

  try {
    const { output } = await llmProviderService.callLLMWithQuery(
      'securelink-ros-extraction',
      { conversationResults },
      { module: 'StandaloneConversationPage', featureName: 'securelink-ros-extraction' }
    );
    const raw = typeof output === 'string' ? output : output?.text ?? output;
    const str = typeof raw === 'string' ? raw : JSON.stringify(raw);
    let parsed = null;
    try {
      parsed = JSON.parse(str);
    } catch (parseErr) {
      const match = str.match(/\[[\s\S]*\]/);
      if (match) {
        parsed = JSON.parse(match[0]);
      } else {
        console.warn('[StandaloneConversationPage] ROS parse failed', parseErr);
      }
    }
    if (Array.isArray(parsed) && parsed.length > 0 && parsed.some((p) => p && (p.system != null || p.systemName != null))) {
      return parsed.map((p) => ({
        system: p.system ?? p.systemName ?? '',
        value: (p.value ?? p.summary ?? '').trim() || 'No complaints',
      }));
    }
  } catch (err) {
    console.warn('[StandaloneConversationPage] ROS extraction failed:', err);
  }

  return null;
}

const StandaloneConversationPage = () => {
  const { dispatchId, code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userData, loading: authLoading } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [dispatch, setDispatch] = useState(null);
  const [patientCMR, setPatientCMR] = useState(null);
  const [error, setError] = useState('');
  const [complete, setComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const conversationTemplate = useMemo(() => dispatch?.templateSnapshot || null, [dispatch]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      try {
        sessionStorage.setItem(
          PENDING_DISPATCH_STORAGE_KEY,
          JSON.stringify({ dispatchId, code })
        );
      } catch (err) {
        console.warn('[StandaloneConversationPage] Could not persist pending dispatch', err);
      }
      navigate(`/auth?returnUrl=${encodeURIComponent(location.pathname)}`, { replace: true });
      return;
    }

    if (userData?.role && !isPatientConversationRole(userData.role)) {
      setLoading(false);
      setError('This conversation link is for patient accounts only.');
      return;
    }

    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const resolved = await validateConversationDispatch(dispatchId, code);
        if (resolved.patientId && resolved.patientId !== user.uid) {
          setError('This conversation was sent to a different patient account.');
          return;
        }

        await markDispatchStarted(dispatchId, user.uid);
        try {
          sessionStorage.removeItem(PENDING_DISPATCH_STORAGE_KEY);
        } catch (storageErr) {
          console.warn('[StandaloneConversationPage] Could not clear pending dispatch', storageErr);
        }
        const normalized = await getNormalizedPatientData(user.uid);
        if (!active) return;
        setDispatch({
          ...resolved,
          patientId: resolved.patientId || user.uid,
          status: 'started',
        });
        setPatientCMR(mapPatientCmr(normalized));
      } catch (err) {
        console.warn('[StandaloneConversationPage] Dispatch validation failed:', err);
        if (active) {
          setError(getDispatchErrorMessage(err));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [authLoading, code, dispatchId, location.pathname, navigate, user, userData?.role]);

  const handleComplete = async (conversationResults) => {
    setSubmitting(true);
    setError('');
    try {
      const rosResults = await extractRosResults(conversationResults);
      await completeConversationDispatch(dispatchId, conversationResults, rosResults);
      setComplete(true);
    } catch (err) {
      console.warn('[StandaloneConversationPage] Completion failed:', err);
      setError('We could not save this conversation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    await handleComplete({
      title: conversationTemplate?.title || 'Health conversation',
      findings: [],
      summary: 'Patient skipped this conversation.',
      transcript: [],
      skipped: true,
      completedAt: new Date().toISOString(),
    });
  };

  if (authLoading || loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Paper elevation={0} sx={{ ...contentCardSx, p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Opening conversation...
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (error && !dispatch) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Paper elevation={0} sx={{ ...contentCardSx, p: 4, maxWidth: 460, textAlign: 'center' }}>
          <ErrorIcon color="warning" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" gutterBottom>Conversation Unavailable</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  if (complete) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Paper elevation={0} sx={{ ...contentCardSx, p: 4, maxWidth: 500, textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 72, mb: 2 }} />
          <Typography variant="h4" gutterBottom>Conversation Complete</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you. Your care team can now review what you shared.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', py: 4 }}>
      <Container maxWidth="md">
        <Paper elevation={0} sx={{ ...contentCardSx, p: 3, mb: 3, textAlign: 'center' }}>
          <ChatOutlinedIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h5" gutterBottom>
            {conversationTemplate?.title || 'Health conversation'}
          </Typography>
          <Chip label="HealthDesk conversation" color="primary" />
        </Paper>

        <Alert severity="info" sx={{ mb: 2 }}>
          Information you provide is summarized for your care team. Call your local emergency number if you have an emergency.
        </Alert>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}

        {submitting ? (
          <Paper elevation={0} sx={{ ...contentCardSx, p: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Saving your conversation...
            </Typography>
          </Paper>
        ) : (
          <CheckInConversation
            conversationTemplate={conversationTemplate}
            patientCMR={patientCMR}
            medicationsReview="none"
            endActions={conversationTemplate?.endActions ?? []}
            suggestedQuestions={conversationTemplate?.suggestedQuestions ?? []}
            onComplete={handleComplete}
            onSkip={handleSkip}
          />
        )}
      </Container>
    </Box>
  );
};

export default StandaloneConversationPage;
